import { INotificationComponent } from './../mixins/action-notification.mixin';
import { IBaseModule } from '@/store/base-module';
import { IEntity } from '@/models/common/entity';
import Vue from 'vue';
import { NoCache } from '../decorators/no-cache';
import { isNull } from '../utils/utils';
import { UnsavedChangesModalResult } from '@/models/ui/unsaved-changed-modal-result';
import { Watch, Inject, Component } from 'vue-property-decorator';
import _ from '@/shared/lodash.global'
import { RecordStatus } from '@/models/common/record-status';
import { UNSAVED_CHANGES_CONFIRMATION_TOKEN } from '../utils/unsaved-changes-token';
import { IFieldDescription } from '@/models/common/field-description';

@Component
export class BaseStoreEditComponent<T extends IEntity> extends Vue implements INotificationComponent {
    @NoCache
    get storeModule(): IBaseModule<T> { throw new Error('`storeModule` is abstract') }

    @Inject(UNSAVED_CHANGES_CONFIRMATION_TOKEN)
    showUnsavedChangesModal: (message: string) => Promise<UnsavedChangesModalResult>

    isLoading = false;
    private deleteNotificationText = null

    @NoCache
    get notificationText(): string { throw new Error('`notificationText` is abstract.') }
    get saveChangesText(): string { return `Do you want to save changes to ${this.notificationText}` }

    get records(): T[] { return this.storeModule.records }
    get selectedItem(): T { return this.storeModule.selectedItem }
    get editingItem(): T { return this.storeModule.editingItem }
    get canAdd(): boolean { return this.storeModule.canAdd }
    get canSave(): boolean { return this.storeModule.canSave }
    get canCancel(): boolean { return this.storeModule.canCancel }
    get canCopy(): boolean { return this.storeModule.canCopy }
    get canRefresh(): boolean { return this.storeModule.canRefresh }
    get canDelete(): boolean { return this.storeModule.canDelete }
    get validationErrors(): any { return this.storeModule.validationErrors }

    get isDirty(): boolean { return [RecordStatus.NEW, RecordStatus.CHANGED].includes(this.storeModule.recordStatus) }

    get isDeleting(): boolean { return this.storeModule.isDeleting }
    set isDeleting(value: boolean) { this.storeModule.isDeleting = value }

    isSelected(record: T): boolean {
        return !isNull(this.editingItem) && this.editingItem.id === record.id
    }

    async selectItem(item: T) {
        if (await this.confirmUnsavedChanges()) {
            const itemDetail = await this.onBeforeSelectItem(item)
            this.storeModule.selectItem(itemDetail)
            Vue.nextTick(() => this.onAfterSelectItem(itemDetail))
        }
    }

    async confirmUnsavedChanges() {
        if (this.isDirty) {
            const result = await this.showUnsavedChangesModal(this.saveChangesText)
            switch (result) {
                case UnsavedChangesModalResult.Cancel:
                    this.onBeforeUnsagedChangesResult(UnsavedChangesModalResult.Cancel)
                    return false
                case UnsavedChangesModalResult.Discard:
                    this.onBeforeUnsagedChangesResult(UnsavedChangesModalResult.Discard)
                    this.storeModule.reset({ resetRecords: false })
                    break
                case UnsavedChangesModalResult.Apply:
                    this.onBeforeUnsagedChangesResult(UnsavedChangesModalResult.Apply)
                    return await this.save() || false

                default:
                    throw new Error(`UnsavedChangesModalResult '${result}' is not supported.`)
            }
        }

        return true;
    }

    copy() {
        this.storeModule.copy()
        Vue.nextTick(() => this.onAfterCopy())
    }

    async save() {
        const isNew = this.editingItem.isNew;
        const notify = isNew
            ? this.$notifyCreated
            : this.$notifyUpdated;

        const proceed = await this.onBeforeSave()
        if (!proceed) { return; }

        const result = await this.storeModule.save()

        this.onAfterSavePrivate(result, isNew)
        notify();

        return true
    }

    cancel() {
        this.storeModule.cancel()
        Vue.nextTick(() => this.onAfterCancel())
    }

    reset() { this.storeModule.reset() }

    updateEditingItemField(e: IFieldDescription) { this.storeModule.updateEditingItemField(e) }

    newRecord(params?: any) {
        this.storeModule.newRecord(params)
        // this.$defaultFocus()
        Vue.nextTick(() => this.onAfterNewRecord())
    }

    // @Catch()
    async deleteRecord(message: string, resetRecords = true): Promise<any> {
        this.isDeleting = (await this.$confirmWarning(message)) || false

        if (!this.isDeleting) { return }

        const originalRecord = _.cloneDeep(this.editingItem)
        this.isLoading = true
        this.deleteNotificationText = 'notificationText' in this
            ? this['notificationText']
            : null

        try {
            if (!await this.onBeforeDelete()) { throw { canceled: true } }

            const deleteHandler = this.storeModule.delete || (() => Promise.resolve())
            await deleteHandler(resetRecords)

            this.notifyDeleted()
            this.onAfterDeletePrivate(originalRecord)
        } catch (e) {
            if (!e.canceled) { throw e }
        } finally {
            this.isDeleting = false
            this.isLoading = false
        }
    }

    private notifyDeleted() {
        if (!isNull(this.deleteNotificationText)) {
            this.$notifyDeleted(this.deleteNotificationText)
        }
    }

    @Watch('isDeleting')
    private onDeletingChange(current: boolean, prev: boolean) {
        if (prev === false && current === true) {
            this.isLoading = true
        }

        if (prev === true && current === false) {
            this.isLoading = false
        }
    }

    protected onAfterSavePrivate(saveOperationResult: any, isNew: boolean) {
        this.onAfterSave(saveOperationResult, isNew)
    }

    protected onAfterDeletePrivate(originalRecord: T) {
        this.onAfterDelete(originalRecord)
    }

    protected async onBeforeSave(): Promise<boolean> { return true; /* virtual method */ }
    protected onAfterSave(saveOperationResult: any, isNew: boolean) { /* virtual method */ }

    protected onAfterNewRecord() { /* virtual method */ }

    protected onAfterCopy() { /* virtual method */ }

    protected async onBeforeDelete(): Promise<boolean> { return true /* virtual method */ }
    protected onAfterDelete(originalRecord: T) { /* virtual method */ }

    protected onBeforeSelectItem(item: T): Promise<T> { return Promise.resolve(item) /* virtual method */ }
    protected onAfterSelectItem(item: T) { /* virtual method */ }

    protected onAfterCancel() { /* virtual method */ }

    protected onBeforeUnsagedChangesResult(result: UnsavedChangesModalResult) { /* virtual method */ }
}
